import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../i18n/LanguageContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || t('compte.connexion.erreur'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      await loginWithGoogle();
      // Google login redirect is handled by the callback page, but we can try to preserve state if possible
      // For now, standard login flow supports redirect
    } catch (err) {
      setError(err.message || t('compte.connexion.erreurGoogle'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-gradient-to-br from-[#F1F2E9] via-[#F2EFE4] to-[#FEFEFA] relative overflow-hidden">
      <Helmet>
        <title>{t('compte.connexion.seoTitre')} | Mirum Games</title>
        <meta name="description" content={t('compte.connexion.seoDescription')} />
      </Helmet>

      {/* Decorative circles */}
      <div className="fixed top-[-50px] left-[-50px] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#E1A72E] to-[#E8C36A] opacity-10 pointer-events-none -z-10" />
      <div className="fixed bottom-[-50px] right-[-50px] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#DCEAE4] to-[#E1A72E] opacity-10 pointer-events-none -z-10" />

      <div className="w-full max-w-[450px] bg-[#FEFEFA] border-3 border-[#DCEAE4] rounded-2xl p-6 md:p-8 shadow-[0_20px_60px_rgba(139,111,71,0.2)] animate-[fadeInUp_0.8s_ease-out] relative z-10">
        {/* Top decoration circle */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-gradient-to-br from-[#E1A72E] to-[#DCEAE4] rounded-full shadow-[0_10px_30px_rgba(212,175,55,0.3)] flex items-center justify-center" />

        {/* Header */}
        <div className="flex flex-col items-center gap-4 md:gap-6 text-center mb-6 md:mb-8 mt-4 md:mt-6">
          <div className="text-6xl md:text-[60px] animate-[floatGentle_2s_ease-in-out_infinite]">🎮</div>
          <h1 className="m-0 text-2xl md:text-[32px] font-black text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif]">{t('compte.connexion.titre')}</h1>
          <p className="m-0 text-sm text-[#2F7E72] italic">{t('compte.connexion.sousTitre')}</p>
        </div>

        {/* Form */}
        <form className="flex flex-col gap-4 md:gap-6 animate-[fadeInUp_0.8s_ease-out_0.1s_backwards]" onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="p-4 rounded-lg bg-red-100/15 border-2 border-red-400 text-red-800 text-[13px] font-semibold text-center animate-[scaleIn_0.3s_ease]">
              {error}
            </div>
          )}

          {/* Email Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              className="px-4 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4] invalid:border-red-400 invalid:bg-red-50"
              placeholder={t('compte.champ.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Password Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-[13px] font-semibold text-[#1F3833] uppercase tracking-wide">
              {t('compte.champ.motDePasse')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="w-full px-4 pr-10 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
                placeholder={t('compte.champ.motDePassePlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-lg text-[#2F7E72] transition-all duration-200 p-1 hover:text-[#E1A72E] active:scale-90"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={t('compte.champ.basculerMotDePasse')}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Remember Me Checkbox */}
          <div className="flex items-center gap-2 text-[13px] text-[#1F3833]">
            <input
              id="remember"
              type="checkbox"
              className="w-4 h-4 cursor-pointer"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="remember" className="cursor-pointer select-none">{t('compte.connexion.seSouvenir')}</label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="px-6 py-3 md:py-[14px] border-none rounded-lg text-sm font-bold uppercase tracking-wide cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#E1A72E] to-[#DCEAE4] text-white shadow-[0_8px_20px_rgba(212,175,55,0.2)] hover:not(:disabled):-translate-y-0.5 hover:not(:disabled):shadow-[0_12px_30px_rgba(212,175,55,0.4)] active:not(:disabled):translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? t('compte.connexion.enCours') : t('compte.connexion.bouton')}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 md:gap-6 my-4 md:my-6">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#DCEAE4] to-transparent" />
          <span className="text-xs text-[#2F7E72] font-semibold uppercase tracking-wide whitespace-nowrap">{t('compte.connexion.ouContinuerAvec')}</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#DCEAE4] to-transparent" />
        </div>

        {/* Social Login */}
        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full px-6 py-3 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm font-bold uppercase tracking-wide cursor-pointer transition-all duration-300 bg-white text-[#1F3833] flex items-center justify-center gap-2 hover:bg-[#F1F2E9] hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(139,111,71,0.15)]"
        >
          <span>🔵</span>
          <span>Google</span>
        </button>

        {/* Footer Links */}
        <div className="flex flex-col gap-3 md:gap-4 text-center mt-6 md:mt-8 pt-4 md:pt-6 border-t-2 border-[#DCEAE4]">
          <p className="text-[13px] text-[#1F3833] m-0">
            {t('compte.connexion.pasDeCompte')}{' '}
            <Link to="/register" className="text-[#E1A72E] no-underline font-bold transition-all duration-300 hover:text-[#DCEAE4] hover:underline">{t('compte.connexion.creerUnCompte')}</Link>
          </p>
          <Link to="#" className="text-xs text-[#2F7E72] no-underline transition-all duration-300 hover:text-[#E1A72E]">
            {t('compte.connexion.motDePasseOublie')}
          </Link>
        </div>
      </div>
    </div>
  );
}
